import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AdTemplateProductModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/adTemplateProduct/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/adTemplateProduct/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/adTemplateProduct/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/adTemplateProduct/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/adTemplateProduct/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/adTemplateProduct/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/adTemplateProduct/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/adTemplateProduct/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/adTemplateProduct/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/adTemplateProduct/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/adTemplateProduct/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/adTemplateProduct/DELETE_FAILURE'

export type AdTemplateProductModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AdTemplateProductModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAdTemplateProducts(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().adTemplateProducts.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)) {
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/ad-template-products', options)
      .then(({ data }: { data: { data: AdTemplateProductModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.AD_TEMPLATE_PRODUCT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAdTemplateProduct(
  adTemplateProduct: Partial<AdTemplateProductModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...adTemplateProduct,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/ad-template-products', options, config)
      .then(({ data }: { data: AdTemplateProductModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_TEMPLATE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAdTemplateProduct(
  adTemplateProduct: UpdateParams<AdTemplateProductModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      ...adTemplateProduct,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/ad-template-products/${adTemplateProduct.id}`, options, config)
      .then(({ data }: { data: AdTemplateProductModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_TEMPLATE_PRODUCT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function setDefaultAdTemplateProduct(
  adTemplateProductId: number,
  isDefault: boolean,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/ad-template-products/${adTemplateProductId}/default?isDefault=${isDefault}`, options, config)
      .then(({ data }: { data: AdTemplateProductModel }) => {
        const normalizedJson = normalize(data, Schemas.AD_TEMPLATE_PRODUCT_ARRAY)

        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkCreateAdTemplateProducts(
  params: { adTemplateIds?: number[], productIds?: number[] },
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/ad-template-products/bulk', options, config)
      .then(({ data }: { data: AdTemplateProductModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AD_TEMPLATE_PRODUCT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAdTemplateProduct(adTemplateProduct: DeleteParams<AdTemplateProductModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/ad-template-products/${adTemplateProduct.id}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: adTemplateProduct.id }
        const normalizedJson = normalize(deletedEntity, Schemas.AD_TEMPLATE_PRODUCT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AdTemplateProductModuleState = initialState,
  action: Action = { type: '' },
): AdTemplateProductModuleState {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
